/**
 * Pay run list, and the "create a run for a week" flow.
 *
 * A run is created for a period, not for a person: the API walks every staff
 * member with approved timesheets in the window and calculates from those. That
 * is why the create modal talks about the week rather than about people, and
 * why it warns when nothing has been approved yet: a run over unapproved
 * timesheets is an empty run, and an empty run looks like a bug.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'wouter';
import { ChevronRight, Lock, Plus, Receipt } from 'lucide-react';
import { api, ApiError, qk } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  addDays,
  fmtDateRange,
  fmtDateTime12,
  fmtMoney,
  today,
  weekStart,
  type IsoDate,
} from '../../lib/time.ts';
import { EmptyState, ErrorState, Field, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { Modal, OutcomeModal } from '../../components/Overlay.tsx';

export default function PayPage() {
  const { tenant } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [creating, setCreating] = useState(false);
  // Defaults to the week just gone, which is the one a boss is almost always
  // paying when they open this screen on a Monday or Tuesday.
  const [periodStart, setPeriodStart] = useState<IsoDate>(() => addDays(weekStart(today()), -7));
  const [failure, setFailure] = useState<string | null>(null);

  const query = useQuery({ queryKey: qk.payRuns(slug), queryFn: () => api.payRuns(slug), enabled: slug !== '' });

  const create = useMutation({
    mutationFn: () => api.createPayRun(slug, periodStart, addDays(periodStart, 6)),
    onSuccess: (res) => {
      setCreating(false);
      void qc.invalidateQueries({ queryKey: qk.payRuns(slug) });
      navigate(`/pay/${res.id}`);
    },
    onError: (err) => {
      setCreating(false);
      setFailure(
        err instanceof ApiError ? err.message : 'Could not create that pay run. Try again in a moment.',
      );
    },
  });

  const runs = useMemo(() => {
    const items = query.data?.items ?? [];
    return [...items].sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  }, [query.data?.items]);

  if (query.isLoading) return <LoadingBlock label="Loading pay runs" />;
  if (query.isError) return <ErrorState error={query.error} what="pay runs" onRetry={() => void query.refetch()} />;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Pay</h1>
          <p className="text-[13px] text-muted">
            {runs.length} {runs.length === 1 ? 'run' : 'runs'}, newest first
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus size={15} aria-hidden="true" />
          New pay run
        </button>
      </header>

      {runs.length === 0 ? (
        <EmptyState
          icon={<Receipt size={20} aria-hidden="true" />}
          title="No pay runs yet"
          body="Approve a week of timesheets, then create a run for that week. Every payslip is calculated from approved hours."
          action={
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              <Plus size={15} aria-hidden="true" />
              New pay run
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          {runs.map((r, i) => (
            <Link
              key={r.id}
              href={`/pay/${r.id}`}
              className="row-link flex items-center gap-3 px-4 py-3.5"
              style={{ borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined }}
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center"
                style={{
                  borderRadius: 'var(--sp-radius-sm)',
                  background: r.status === 'finalised' ? 'var(--sp-accent-soft)' : 'var(--sp-surface-2)',
                  color: r.status === 'finalised' ? 'var(--sp-accent-strong)' : 'var(--sp-text-faint)',
                  border: '1px solid var(--sp-border)',
                }}
                aria-hidden="true"
              >
                {r.status === 'finalised' ? <Lock size={16} /> : <Receipt size={16} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14.5px] font-bold text-ink">
                  {fmtDateRange(r.periodStart, r.periodEnd)}
                </span>
                <span className="block truncate text-[12.5px] text-muted">
                  {r.status === 'finalised'
                    ? `Finalised ${fmtDateTime12(r.finalisedAt)}`
                    : 'Draft, not yet finalised'}
                  {r.staffCount != null ? ` · ${r.staffCount} on this run` : ''}
                </span>
              </span>
              {r.totalGross != null ? (
                <span className="tabular hidden text-right sm:block">
                  <span className="block text-[14px] font-extrabold text-ink">{fmtMoney(r.totalGross)}</span>
                  <span className="block text-[11.5px] text-faint">gross</span>
                </span>
              ) : null}
              <span
                className={r.status === 'finalised' ? 'chip chip-accent' : 'chip chip-neutral'}
                style={{ minWidth: 76, justifyContent: 'center' }}
              >
                {r.status === 'finalised' ? 'Finalised' : 'Draft'}
              </span>
              <ChevronRight size={16} aria-hidden="true" className="row-chev" style={{ color: 'var(--sp-text-faint)' }} />
            </Link>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New pay run"
        subtitle="One week at a time, calculated from approved timesheets."
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? <Spinner size={15} label="Calculating" /> : <Receipt size={15} aria-hidden="true" />}
              Calculate this week
            </button>
          </>
        }
      >
        <Field label="Week starting" id="run-week" hint="Any day in the week works. It snaps to the Monday.">
          <input
            id="run-week"
            className="field"
            type="date"
            value={periodStart}
            onChange={(e) => e.target.value && setPeriodStart(weekStart(e.target.value))}
          />
        </Field>
        <p className="text-[13.5px] leading-relaxed text-muted">
          Covering {fmtDateRange(periodStart, addDays(periodStart, 6))}. Only approved timesheets are included, so
          approve the week first if you have not already. Creating a run again for the same week replaces the draft
          rather than duplicating it.
        </p>
      </Modal>

      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not create the pay run"
        message={failure ?? ''}
      />
    </div>
  );
}
